import { useEffect, useState } from 'react';
import { getTaskAssignmentsWithSchedules } from '../../../api/timeAssignments/getTaskAssignmentsWithSchedules';
import { IListTimeAssignmentsResponse } from '../../../api/timeAssignments/types';
import { useAuth } from '../../authentication/hooks/useAuth';
import { Icon } from '../../../assets/icons';
import styles from '../styles/home.module.scss';
import { ITask } from '@/api/tasks/types';

interface WeeklyTasksSummaryProps {
  onTaskClick: (task: ITask) => void;
}

export const WeeklyTasksSummary = ({ onTaskClick }: WeeklyTasksSummaryProps) => {
  const { user } = useAuth();
  const [userAssignments, setUserAssignments] = useState<IListTimeAssignmentsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Obtener la fecha de hoy en formato YYYY-MM-DD
  const getTodayDate = () => {
    const now = new Date();
    const yyyy = now.getFullYear();
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const dd = String(now.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };

  useEffect(() => {
    const fetchTasks = async () => {
      try {
        setLoading(true);
        const today = getTodayDate();
        const data = await getTaskAssignmentsWithSchedules({ scheduleFrom: today, scheduleTo: today });
        // El endpoint devuelve un array de usuarios, filtramos por el username del usuario activo
        const username = user?.data?.username;
        if (username) {
          const found = Array.isArray(data)
            ? data.find((u) => u.username === username)
            : null;
          setUserAssignments(found || null);
        } else {
          setUserAssignments(null);
        }
      } catch (err) {
        setError('Error al cargar las tareas del día');
        console.error('Error fetching tasks:', err);
      } finally {
        setLoading(false);
      }
    };

    if (user?.data?.username) {
      fetchTasks();
    }
  }, [user]);

  // const formatDateRange = (dateString: string) => {
  //   const date = new Date(dateString);
  //   return date.toLocaleDateString('es-ES', {
  //     month: 'short',
  //     day: 'numeric'
  //   });
  // };

  const handleCardClick = (assignment: IListTimeAssignmentsResponse['assignments'][0]) => {
    const task: Partial<ITask> = {
      id: String(assignment.task_id),
      title: assignment.task_title,
    };
    onTaskClick(task as ITask);
  };

  if (loading) {
    return (
      <div className={styles.weeklyTasksSummary}>
        <div className={styles.weeklyTasksSummary__loading}>
          <Icon name="reindex" />
          <p>Cargando tareas del día...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.weeklyTasksSummary}>
        <div className={styles.weeklyTasksSummary__error}>
          <Icon name="more" />
          <p>{error}</p>
        </div>
      </div>
    );
  }

  const assignments = userAssignments?.assignments || [];

  return (
    <div className={styles.weeklyTasksSummary}>
      <div className={styles.weeklyTasksSummary__header}>
        <h2 className={styles.weeklyTasksSummary__title}>
          Tareas del Día
        </h2>
        <span className={styles.weeklyTasksSummary__count}>
          {assignments.length} tareas
        </span>
      </div>

      {assignments.length === 0 ? (
        <div className={styles.weeklyTasksSummary__empty}>
          <Icon name="save" />
          <p>No hay tareas programadas para hoy</p>
        </div>
      ) : (
        <div className={styles.weeklyTasksSummary__grid}>
          {assignments.map((assignment) => (
            <div
              key={assignment.task_assignment_id}
              className={styles.taskCard}
              onClick={() => handleCardClick(assignment)}
            >
              <div className={styles.taskCard__header}>
                <div className={styles.taskCard__statusIcon}>
                  <Icon name="campaing" />
                </div>
                <h3 className={styles.taskCard__title}>
                  {assignment.task_title || 'Sin título'}
                </h3>
              </div>

              <div className={styles.taskCard__tags}>
                <span className={`${styles.taskCard__priorityTag} ${styles.taskCard__priorityTagMedium}`}>
                  Medium
                </span>
                <span className={styles.taskCard__versionTag}>
                  Version 1.0
                </span>
              </div>

              <div className={styles.taskCard__footer}>
                <div className={styles.taskCard__user}>
                  <div className={styles.taskCard__avatar}>
                    <Icon name="user" />
                  </div>
                  <span className={styles.taskCard__dateRange}>
                    Sin fechas
                  </span>
                </div>
                <div className={styles.taskCard__actions}>
                  <span className={styles.taskCard__commentCount}>
                    {assignment.schedules.length}
                  </span>
                  <Icon name="more" />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}; 